import { useEffect, useState } from "react";
import { AD_CLIENT_ID, setAdConsent } from "./AdBanner.tsx";
import { cn } from "./cn.ts";

/**
 * Google Funding Choices ("Privacy & messaging" in the AdSense UI) is the
 * Google-certified CMP this suite uses. It is deliberately loaded *eagerly*,
 * unlike adsbygoogle.js:
 *
 *   - A CMP has to run in order to ask the question, so loading it before
 *     consent is the one thing that is not a consent violation. It sets no
 *     advertising cookies of its own.
 *   - adsbygoogle.js stays unloaded until this module reports a "yes", which is
 *     what <AdBanner> gates on. Google's own integration lets the ad tag pull
 *     the CMP in itself; we don't, because the privacy policy promises that no
 *     advertising code loads before the visitor has had a chance to choose.
 *
 * Requires a GDPR message to be created *and published* under AdSense →
 * Privacy & messaging. Without one, the script 204s, no __tcfapi ever appears,
 * and consent stays denied — i.e. no ads anywhere. That is the safe direction
 * to fail, but it does mean the dashboard step is not optional.
 */

/** IAB TCF vendor id for Google Advertising Products. */
const GOOGLE_VENDOR_ID = 755;

/**
 * TCF purpose 1, "store and/or access information on a device". AdSense cannot
 * run without it, so it is the only purpose we treat as load-bearing — asking
 * for more would deny ads to visitors who consented to exactly what we need.
 */
const STORAGE_PURPOSE = 1;

/** How long to wait for the CMP to install __tcfapi before giving up on it. */
const CMP_TIMEOUT_MS = 4000;

/** How long <AdConsentLink> keeps watching for the CMP before staying hidden. */
const REVOCATION_TIMEOUT_MS = 10_000;

const POLL_MS = 100;

/** Fired on `window` once the CMP exposes its "change your choice" dialog. */
export const CMP_READY_EVENT = "suite:cmpready";

interface TcfConsents {
  consents?: Record<number, boolean | undefined>;
}

interface TcData {
  eventStatus?: "tcloaded" | "cmpuishown" | "useractioncomplete";
  gdprApplies?: boolean;
  purpose?: TcfConsents;
  vendor?: TcfConsents;
}

type TcfApi = (
  command: string,
  version: number,
  callback: (data: TcData, success: boolean) => void,
  parameter?: unknown,
) => void;

declare global {
  interface Window {
    __tcfapi?: TcfApi;
    googlefc?: { showRevocationMessage?: () => void };
    __suiteAdConsentInit?: boolean;
  }
}

/** The AdSense publisher id (`pub-…`), which is the ad client minus its `ca-`. */
function publisherId(): string {
  return (AD_CLIENT_ID ?? "").replace(/^ca-/, "");
}

/**
 * Boot the consent manager. Call once, from the app entry, *after* the React
 * root is created: granting consent synchronously during module evaluation
 * would flip <AdBanner> on before hydration and mismatch the prerendered HTML.
 *
 * No-ops when no ad client is configured — an unmonetised build has nothing to
 * ask permission for, and showing a consent dialog anyway would be absurd.
 */
export function initAdConsent(): void {
  if (typeof window === "undefined") return;
  if (!AD_CLIENT_ID) return;
  if (window.__suiteAdConsentInit) return;
  window.__suiteAdConsentInit = true;

  loadFundingChoices();
  listenForTcf();
  watchForRevocationApi();
}

function loadFundingChoices(): void {
  const id = "googlefc-js";
  if (document.getElementById(id)) return;
  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = `https://fundingchoicesmessages.google.com/i/${publisherId()}?ers=1`;
  document.head.appendChild(script);
  signalGooglefcPresent();
}

/**
 * The hidden `googlefcPresent` frame from Google's official snippet. It is how
 * Google detects that a CMP is installed on the page; without it the account
 * can be flagged as having no consent solution even though this one works.
 */
function signalGooglefcPresent(): void {
  const frames = window.frames as unknown as Record<string, Window | undefined>;
  if (frames["googlefcPresent"]) return;
  if (!document.body) {
    window.setTimeout(signalGooglefcPresent, 0);
    return;
  }
  const iframe = document.createElement("iframe");
  iframe.name = "googlefcPresent";
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "width:0;height:0;border:0;z-index:-1000;left:-1000px;top:-1000px;position:absolute;display:none";
  document.body.appendChild(iframe);
}

/** Polls for the CMP's TCF stub, then subscribes to its consent decisions. */
function listenForTcf(): void {
  const deadline = Date.now() + CMP_TIMEOUT_MS;
  const tick = () => {
    const tcfapi = window.__tcfapi;
    if (typeof tcfapi === "function") {
      subscribe(tcfapi);
      return;
    }
    // Timed out: the CMP is blocked, or no message is published. Consent is
    // unknown rather than refused, but unknown has to mean no ads.
    if (Date.now() >= deadline) return;
    window.setTimeout(tick, POLL_MS);
  };
  tick();
}

function subscribe(tcfapi: TcfApi): void {
  tcfapi("addEventListener", 2, (data, success) => {
    if (!success || !data) return;
    // Outside the EEA/UK/CH the CMP shows nothing and reports gdprApplies:false.
    // Nothing to wait for, so ads may run; Google applies the US state-privacy
    // signals itself from inside the ad tag.
    if (data.gdprApplies === false) {
      setAdConsent(true);
      return;
    }
    // "cmpuishown" means the dialog is open and the answer is still pending.
    if (data.eventStatus !== "tcloaded" && data.eventStatus !== "useractioncomplete") return;
    setAdConsent(
      data.purpose?.consents?.[STORAGE_PURPOSE] === true &&
        data.vendor?.consents?.[GOOGLE_VENDOR_ID] === true,
    );
  });
}

/** True once the CMP can reopen its dialog so a visitor can change their mind. */
export function canShowAdConsentOptions(): boolean {
  return (
    typeof window !== "undefined" && typeof window.googlefc?.showRevocationMessage === "function"
  );
}

export function showAdConsentOptions(): void {
  window.googlefc?.showRevocationMessage?.();
}

function watchForRevocationApi(): void {
  const deadline = Date.now() + REVOCATION_TIMEOUT_MS;
  const tick = () => {
    if (canShowAdConsentOptions()) {
      window.dispatchEvent(new Event(CMP_READY_EVENT));
      return;
    }
    if (Date.now() >= deadline) return;
    window.setTimeout(tick, POLL_MS);
  };
  tick();
}

/**
 * Footer link that reopens the consent dialog. Renders nothing until the CMP is
 * actually there to reopen — which is most of the time, since visitors outside
 * the EEA/UK/CH are never shown a dialog and have no choice to revisit.
 *
 * The privacy policy promises this link exists wherever a dialog was shown, so
 * it belongs in every footer that can carry an ad.
 */
export function AdConsentLink({ className }: { className?: string }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (canShowAdConsentOptions()) {
      setReady(true);
      return;
    }
    const onReady = () => setReady(true);
    window.addEventListener(CMP_READY_EVENT, onReady);
    return () => window.removeEventListener(CMP_READY_EVENT, onReady);
  }, []);

  if (!ready) return null;

  return (
    <button type="button" onClick={showAdConsentOptions} className={cn("link", className)}>
      Ad privacy
    </button>
  );
}
